import axios from 'axios';

import {
	COREAPI,
	encodeData,
	// encodeData,
	getUserData,
	ISADMINLOGGED,
	ISSESSION,
	onLogout,
} from '../../constants/global';
import { extractApiErrorMessage } from '../../lib/api-error';
import { globalType } from '../types';

export const header = () => ({
	'Content-Type': 'application/json',
	'Access-Control-Allow-Origin': '*',
	'Authorization': getUserData().token ? `Bearer ${getUserData().token}` : '',
	'PartyId': getUserData().user?.PartyId,
});

export const headerMultipart = () => ({
	Authorization: getUserData().token ? `Bearer ${getUserData().token}` : '',
});

let source: any;

const cancelRequest = () => {
	if (source) {
		source.cancel();
	}
	const CancelToken = axios.CancelToken;
	source = CancelToken.source();
};

const TIMEOUT = 10000 * 30;

type I_API_REQUEST = (
	url: string | undefined,
	headers: Record<string, any>,
	data: any,
	method: 'get' | 'post' | 'put' | 'delete' | 'patch',
	timeout: number | null,
	validateStatus?: (status: number) => boolean,
	dispatch?: any,
	dataError?: string,
	onFailure?: (err: any) => void,
	dataLoading?: string,
	dataAction?: string,
	onSuccess?: (res: any) => void,
	onUploadProgress?: (pct: number) => void
) => Promise<any>;

const buildProgressHandler = (onUploadProgress?: (pct: number) => void) =>
	onUploadProgress
		? (e: { loaded: number; total?: number }) => {
				if (e.total) onUploadProgress(Math.round((e.loaded / e.total) * 100));
			}
		: undefined;

const statusFunction = (onStatus?: (res: any) => void, res?: any) => {
	if (typeof onStatus === 'function') onStatus(res);
};

const apiRespond = (dispatch: any, type: any, payload: any, onStatus?: (res: any) => void) => {
	dispatch({ type, payload });
	statusFunction(onStatus, payload);
};

const apiRequest: I_API_REQUEST = async (
	url,
	headers,
	data,
	method,
	timeout,
	validateStatus,
	dispatch,
	dataError,
	onFailure,
	dataLoading,
	dataAction,
	onSuccess,
	onUploadProgress
) => {
	cancelRequest();
	try {
		if (!ISSESSION()) {
			// refresh token
			if (!ISADMINLOGGED()) {
				onLogout();
				window.location.reload();
			} else {
				await axios({
					url: `${COREAPI}/api/v1/Auth/extend-session`,
					method: 'post',
					timeout: timeout || 0,
					headers: {
						'Content-Type': 'application/json',
						'Access-Control-Allow-Origin': '*',
						'Authorization': getUserData().refreshToken ? `Bearer ${getUserData().refreshToken}` : '',
					},
				}).then((res) => {
					if (res.status >= 200 && res.status < 300) {
						encodeData(res.data?.data?.token, res.data?.data?.refreshToken);
					}
				});
			}
		} else {
			await axios({
				url,
				method,
				data: data || {},
				timeout: timeout || 0,
				validateStatus,
				headers,
				onUploadProgress: buildProgressHandler(onUploadProgress),
			}).then((res) => {
				if (res.status >= 200 && res.status < 300) {
					apiRespond(dispatch, dataAction, res.data, onSuccess);
				} else {
					// A rejection arrives either as the house envelope (`message`) or as ASP.NET
					// ProblemDetails (`errors` per field, no `message`). Reading only `message`
					// used to drop every validation failure into the generic catch below.
					const reason = extractApiErrorMessage(res.data);
					if (reason) {
						apiRespond(dispatch, globalType.notificationGlobal.dataAction, { notice: reason, status: false }, onFailure);
					} else {
						throw new Error(`Request failed with status ${res.status}`);
					}
				}
			});
		}
	} catch (error) {
		if (axios.isCancel(error)) {
			console.log('Request canceled', error.message);
		} else {
			apiRespond(
				dispatch,
				globalType.notificationGlobal.dataAction,
				{
					notice: 'Something went wrong',
					status: false,
				},
				onFailure
			);
		}
	} finally {
		dispatch({ type: dataLoading, payload: false });
	}
};

const apiAuthRequest: I_API_REQUEST = async (
	url,
	headers,
	data,
	method,
	timeout,
	validateStatus,
	dispatch,
	dataError,
	onFailure,
	dataLoading,
	dataAction,
	onSuccess,
	onUploadProgress
) => {
	cancelRequest();
	try {
		const res = await axios({
			url,
			method,
			data: data || {},
			timeout: timeout || 0,
			validateStatus,
			headers,
			onUploadProgress: buildProgressHandler(onUploadProgress),
		});
		if (res.status >= 200 && res.status < 300) {
			apiRespond(dispatch, dataAction, res.data, onSuccess);
		} else {
			// Same two rejection shapes as apiRequest — see extractApiErrorMessage.
			const reason = extractApiErrorMessage(res.data);
			if (reason) {
				apiRespond(dispatch, globalType.notificationGlobal.dataAction, { notice: reason, status: false }, onFailure);
			} else {
				throw new Error(`Request failed with status ${res.status}`);
			}
		}
	} catch (error) {
		if (axios.isCancel(error)) {
			console.log('Request canceled', error.message);
		} else {
			apiRespond(
				dispatch,
				globalType.notificationGlobal.dataAction,
				{
					notice: 'Something went wrong',
					status: false,
				},
				onFailure
			);
		}
	} finally {
		dispatch({ type: dataLoading, payload: false });
	}
};

export const getErrMsg = (error: any) => {
	if (error != null) {
		const { data } = error.response || {};
		if (typeof data === 'object') {
			const { message } = data || {};
			return message;
		}
		return data;
	}
	return '';
};

export interface I_ACTION_TYPE {
	dataLoading: string;
	dataAction: string;
	dataError: string;
}

interface I_HTTPMETHOD {
	apiData: {
		url: string;
		header: Record<string, any>;
		customurl?: string;
		data?: any;
	};
	actionType: I_ACTION_TYPE;
	onSuccess?: (res: any) => void;
	onFailure?: (err: any) => void;
	auth?: boolean;
	onUploadProgress?: (pct: number) => void;
	/**
	 * Axios `validateStatus`. Omit (the default) and axios throws on any non-2xx, so the
	 * catch in `apiRequest` reports a generic 'Something went wrong' and the server's real
	 * `message` is discarded. Pass `() => true` when a 4xx body is *expected and actionable*
	 * — e.g. the V2 result-create endpoints, whose duplicate check returns 400 with a message
	 * the agent needs to read. `httpGetMethod` already hardcodes `() => true`.
	 *
	 * With `() => true`, a non-2xx carrying `message` dispatches a global notification and
	 * calls `onFailure({ notice: <server message>, status: false })`.
	 */
	validateStatus?: (status: number) => boolean;
}

export const httpGetMethod = ({
	apiData: { url, header, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
	auth,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : COREAPI + '' + url;
	return async (dispatch: any) => {
		dispatch({ type: dataLoading, payload: true });
		const req = auth ? apiAuthRequest : apiRequest;
		await req(
			requesturl,
			header,
			null,
			'get',
			null,
			() => true,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};

export const httpPostMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
	auth,
	validateStatus,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : COREAPI + '' + url;
	return async (dispatch: any) => {
		dispatch({ type: dataLoading, payload: true });
		const req = auth ? apiAuthRequest : apiRequest;
		await req(
			requesturl,
			header,
			data,
			'post',
			TIMEOUT,
			validateStatus,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};

export const httpUploadMediaMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
	auth,
	onUploadProgress,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : COREAPI + '' + url;
	return async (dispatch: any) => {
		dispatch({ type: dataLoading, payload: true });
		const req = auth ? apiAuthRequest : apiRequest;
		await req(
			requesturl,
			header,
			data,
			'post',
			TIMEOUT,
			undefined,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess,
			onUploadProgress
		);
	};
};

export const httpPutVideoMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
	auth,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : COREAPI + '' + url;
	return async (dispatch: any) => {
		dispatch({ type: dataLoading, payload: true });
		const req = auth ? apiAuthRequest : apiRequest;
		await req(
			requesturl,
			header,
			data,
			'put',
			TIMEOUT,
			undefined,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};

export const httpDeleteMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
	auth,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : COREAPI + '' + url;
	return async (dispatch: any) => {
		dispatch({ type: dataLoading, payload: true });
		const req = auth ? apiAuthRequest : apiRequest;
		await req(
			requesturl,
			header,
			data,
			'delete',
			TIMEOUT,
			undefined,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};

export const clearHttp = (type: string) => (dispatch: any) => {
	dispatch({ type: type + 'Loading', payload: false });
	dispatch({ type, payload: [] });
	dispatch({ type: type + 'Error', payload: '' });
};

export const clearHttpByValue =
	({
		actionType: { dataLoading, dataAction, dataError },
		dataActionValue,
	}: {
		actionType: I_ACTION_TYPE;
		dataActionValue: any;
	}) =>
	(dispatch: any) => {
		dispatch({ type: dataLoading, payload: false });
		dispatch({ type: dataAction, payload: dataActionValue });
		dispatch({ type: dataError, payload: '' });
	};

export const httpPutMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
	auth,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : COREAPI + '' + url;
	return async (dispatch: any) => {
		dispatch({ type: dataLoading, payload: true });
		const req = auth ? apiAuthRequest : apiRequest;
		await req(
			requesturl,
			header,
			data,
			'put',
			TIMEOUT,
			undefined,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};

export const httpPatchMethod = ({
	apiData: { url, header, data, customurl },
	actionType: { dataLoading, dataAction, dataError },
	onSuccess,
	onFailure,
}: I_HTTPMETHOD) => {
	const requesturl = customurl != null ? customurl : COREAPI + '' + url;
	return async (dispatch: any) => {
		dispatch({ type: dataLoading, payload: true });
		await apiRequest(
			requesturl,
			header,
			data,
			'patch',
			TIMEOUT,
			undefined,
			dispatch,
			dataError,
			onFailure,
			dataLoading,
			dataAction,
			onSuccess
		);
	};
};
